import React from 'react';
import styled from 'styled-components';

import { TITLE_MAX_LENGTH, DESCRIPTION_MAX_LENGTH } from './seoScore';

/**
 * Approximation of a Google result. The point isn't pixel fidelity — it's that an editor
 * can see their title get cut off, which no character counter communicates as well.
 */

const Container = styled.div`
  font-family: arial, sans-serif;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 14px 16px;
  max-width: 600px;

  .breadcrumb {
    color: #202124;
    font-size: 12px;
    line-height: 18px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .favicon {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #cca274;
    color: white;
    font-size: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
  }

  .url {
    color: #4d5156;
  }

  .title {
    color: #1a0dab;
    font-size: 20px;
    line-height: 26px;
    margin-top: 4px;
    /* Google shows one line and elides. */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .description {
    color: #4d5156;
    font-size: 14px;
    line-height: 22px;
    margin-top: 4px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .placeholder {
    color: #9aa0a6;
    font-style: italic;
  }
`;

const SITE_URL = 'https://ancana.co';

export interface SerpPreviewProps {
  title?: string | null;
  description?: string | null;
  /** Path of the page, e.g. `/listing/casa-uno`. */
  path?: string | null;
  locale?: string;
}

const truncate = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;

const SerpPreview = ({ title, description, path, locale }: SerpPreviewProps) => {
  const localePrefix = locale && locale !== 'en' ? `/${locale}` : '';
  const displayUrl = `${SITE_URL}${localePrefix}${path || ''}`
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '');

  return (
    <Container>
      <div className="breadcrumb">
        <span className="favicon">A</span>
        <span>
          <div style={{ fontWeight: 500 }}>Ancana</div>
          <div className="url">{displayUrl}</div>
        </span>
      </div>
      <div className="title">
        {title ? (
          truncate(title, TITLE_MAX_LENGTH + 10)
        ) : (
          <span className="placeholder">No title — a generated one will be used</span>
        )}
      </div>
      <div className="description">
        {description ? (
          truncate(description, DESCRIPTION_MAX_LENGTH + 20)
        ) : (
          <span className="placeholder">
            No description — Google will pick a snippet from the page body
          </span>
        )}
      </div>
    </Container>
  );
};

export default SerpPreview;
